import { useEffect, useState } from "react";
import {
  ChevronDown, ChevronUp, CreditCard, Eye, EyeOff, FileText, Heading1, Image,
  LayoutTemplate, Link2, MousePointerClick, Navigation, PanelBottom, Square, Trash2, Type,
} from "lucide-react";
import { useBuilder } from "./context";
import { addMove, getLabel, parentEidOf, setProp as setOvProp } from "./overrides";

const ICONS = {
  page: FileText, section: LayoutTemplate, navigation: Navigation, footer: PanelBottom,
  heading: Heading1, text: Type, image: Image, button: MousePointerClick,
  link: Link2, form: CreditCard, card: Square, container: Square,
};

export default function LayersPanel() {
  const b = useBuilder();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const root = b.rootRef.current;
    if (!root) return;
    const list = Array.from(root.querySelectorAll("[data-eid]"))
      .filter((el) => !el.closest("[data-builder-ui]"))
      .map((el) => {
        let d = 0;
        let p = el.parentElement;
        while (p && p !== root) {
          if (p.dataset?.eid) d++;
          p = p.parentElement;
        }
        return { eid: el.dataset.eid, type: el.dataset.etype || "container", label: getLabel(el), depth: d, hidden: !!b.overrides.elements?.[el.dataset.eid]?.hidden };
      });
    setRows(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.tick, b.remountKey, b.overrides]);

  const el = (eid) => b.rootRef.current?.querySelector(`[data-eid="${CSS.escape(eid)}"]`);

  const moveRow = (eid, dir) => {
    const node = el(eid);
    if (!node) return;
    const parent = node.parentElement;
    const sibs = Array.from(parent.querySelectorAll(":scope > [data-eid]"));
    const i = sibs.indexOf(node);
    const j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    parent.insertBefore(node, dir < 0 ? sibs[j] : sibs[j].nextSibling);
    const ns = Array.from(parent.querySelectorAll(":scope > [data-eid]"));
    b.commit(addMove(b.ovRef.current, eid, parentEidOf(parent), ns.indexOf(node)), true);
  };

  const pick = (eid) => {
    b.select(eid);
    el(eid)?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  return (
    <aside className="w-60 shrink-0 overflow-y-auto border-r border-white/10 bg-[#141416]" data-builder-ui data-testid="layers-panel">
      <p className="border-b border-white/10 px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500">Layers</p>
      <div className="py-1">
        {rows.map((r) => {
          const Icon = ICONS[r.type] || Square;
          const active = b.selected === r.eid;
          return (
            <div
              key={r.eid}
              className={`group flex items-center gap-1 pr-1 text-xs transition-colors ${active ? "bg-sky-500/20 text-white" : "text-neutral-400 hover:bg-white/5 hover:text-white"}`}
              style={{ paddingLeft: `${8 + r.depth * 12}px` }}
            >
              <button onClick={() => pick(r.eid)} className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left" data-testid={`layer-${r.eid}`}>
                <Icon className="h-3 w-3 shrink-0 opacity-60" />
                <span className={`truncate ${r.hidden ? "opacity-40 line-through" : ""}`}>{r.label}</span>
              </button>
              <span className={`flex shrink-0 items-center ${active ? "" : "opacity-0 group-hover:opacity-100"}`}>
                <button onClick={() => moveRow(r.eid, -1)} className="p-0.5 hover:text-white" title="Move up" data-testid={`layer-up-${r.eid}`}><ChevronUp className="h-3 w-3" /></button>
                <button onClick={() => moveRow(r.eid, 1)} className="p-0.5 hover:text-white" title="Move down" data-testid={`layer-down-${r.eid}`}><ChevronDown className="h-3 w-3" /></button>
                <button
                  onClick={() => b.commit(setOvProp(b.ovRef.current, r.eid, "hidden", !r.hidden))}
                  className="p-0.5 hover:text-white"
                  title={r.hidden ? "Show" : "Hide"}
                  data-testid={`layer-hide-${r.eid}`}
                >
                  {r.hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
                <button
                  onClick={() => { b.commit(setOvProp(b.ovRef.current, r.eid, "deleted", true), true); if (b.selected === r.eid) b.select(null); }}
                  className="p-0.5 hover:text-red-400"
                  title="Delete"
                  data-testid={`layer-delete-${r.eid}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
